// FINANCE FORK: draft-buffered category-exclusion popover, shared between the
// Sankey drill-down toolbar and the dashboard-numbers Settings section.
import { useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Popover } from '@actual-app/components/popover';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import type {
  CategoryEntity,
  CategoryGroupEntity,
} from '@actual-app/core/types/models';

import { CategorySelector } from '#components/reports/CategorySelector';

type ExcludeCategoriesButtonProps = {
  groupedCategories: CategoryGroupEntity[];
  categories: CategoryEntity[];
  excludedCategories: string[];
  onChange: (next: string[]) => void;
  title?: string;
  placement?: 'bottom start' | 'bottom end';
};

export function ExcludeCategoriesButton({
  groupedCategories,
  categories,
  excludedCategories,
  onChange,
  title,
  placement = 'bottom end',
}: ExcludeCategoriesButtonProps) {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  // Buffer selections while the popover is open — applying on close collapses
  // N checkbox toggles into a single refetch instead of one per click.
  const [draft, setDraft] = useState<string[] | null>(null);

  const effectiveExcluded = draft ?? excludedCategories;
  const selectedEntities = useMemo<CategoryEntity[]>(() => {
    const selected = new Set(effectiveExcluded);
    return categories.filter(c => selected.has(c.id));
  }, [categories, effectiveExcluded]);
  const label =
    effectiveExcluded.length > 0
      ? t('Excluding {{count}}', { count: effectiveExcluded.length })
      : t('Exclude categories');

  const openPopover = () => {
    setDraft(excludedCategories);
    setIsOpen(true);
  };
  const closePopover = () => {
    setIsOpen(false);
    if (draft) {
      const prev = new Set(excludedCategories);
      const changed =
        draft.length !== excludedCategories.length ||
        draft.some(id => !prev.has(id));
      if (changed) onChange(draft);
      setDraft(null);
    }
  };

  return (
    <>
      <Button ref={triggerRef} onPress={openPopover}>
        {label}
      </Button>
      <Popover
        triggerRef={triggerRef}
        placement={placement}
        isOpen={isOpen}
        onOpenChange={closePopover}
      >
        <View
          style={{
            padding: 12,
            width: 320,
            maxHeight: 480,
          }}
        >
          <Text style={{ fontWeight: 600, marginBottom: 8 }}>
            {title ?? t('Exclude from chart')}
          </Text>
          <Text
            style={{
              color: theme.pageTextSubdued,
              fontSize: 12,
              marginBottom: 8,
            }}
          >
            <Trans>
              Hide categories that distort the picture — e.g. mis-classified
              transfers into off-budget accounts.
            </Trans>
          </Text>
          <CategorySelector
            categoryGroups={groupedCategories}
            selectedCategories={selectedEntities}
            setSelectedCategories={next => setDraft(next.map(c => c.id))}
          />
        </View>
      </Popover>
    </>
  );
}
